<script setup lang="ts">
import type { SolrParams } from '#shared/solr'
import type { SearchContext } from '~/composables/document-search/strategies/types'
import type { BaseJournal } from '~/utils/solr/journal'
import { faArrowLeft } from '@fortawesome/pro-light-svg-icons'
import { mergeLabelIntoHeading, normalizeJatsWhitespace } from '#shared/utils/jats-heading'
import { useJournalArticleSearchStrategy } from '~/composables/document-search/strategies/useJournalArticleSearchStrategy'
import { useTrackLastActivity } from '~/composables/useLastActivity'
import { journalSectionTitleWithoutFootnote } from '~/utils/journals/section-title'
import { trackableJournalArticleId } from '~/utils/pendo/content-id'
import { trackableLegalAreas } from '~/utils/pendo/legal-areas'
import { getJournalAbstractData, getJournalArticleTypeLabel } from '~/utils/solr/journal-utils'

const props = defineProps<{
  variant: 'issue' | 'volume'
  publisherId: string
  articleNumber: string
  issueNumber?: string
  volume?: string
  issue?: string
}>()

const route = useRoute()
const { $parseStringToDocument } = useNuxtApp()

const { activeView, open, back, reset } = useReadingView('journal-view-stack')
const { sidebarExpanded } = useReadingSidebarDefault()

reset()
onScopeDispose(() => {
  reset()
})

const hasLicense = useHasContentLicense()
const { isDark: readingDarkMode, toggle: toggleReadingDarkMode } = useReadingMode()
const toast = getToast()

const publisherId = computed(() => props.publisherId)
const articleNumber = computed(() => props.articleNumber)
const issueNumber = computed(() => props.issueNumber ?? '')
const volume = computed(() => props.volume ?? '')
const issue = computed(() => props.issue ?? '')

const journalQuery = computed(() => {
  if (props.variant === 'issue') {
    if (!publisherId.value || !issueNumber.value) {
      return {} as SolrParams
    }

    return {
      publisherId: publisherId.value,
      issue: issueNumber.value,
    } as unknown as SolrParams
  }

  if (!publisherId.value || !volume.value || !issue.value) {
    return {} as SolrParams
  }

  return {
    publisherId: publisherId.value,
    volume: volume.value,
    issue: issue.value,
  } as unknown as SolrParams
})

const { data: journalData, isLoading, error } = useSolrQueryV2(journalQuery, 'journal', 'journals')

const journal = computed<BaseJournal | null>(
  () => (journalData.value?.response?.docs?.[0] ?? null) as BaseJournal | null,
)

function getIdTail(id: string): string {
  const parts = id.split('/')
  return parts[parts.length - 1] ?? id
}

const article = computed(() => {
  const currentJournal = journal.value
  if (!currentJournal) {
    return null
  }

  const fromArticles = (currentJournal.articles ?? []).find(
    (item) => getIdTail(item.id) === articleNumber.value,
  )
  if (fromArticles) {
    return fromArticles
  }

  return currentJournal.leder && getIdTail(currentJournal.leder.id) === articleNumber.value
    ? currentJournal.leder
    : null
})

function extractLeadingBodyLabel(bodytext?: string): string | undefined {
  if (!bodytext?.trim()) {
    return undefined
  }

  const match = bodytext.match(/^\s*<label\b[^>]*>([\s\S]*?)<\/label>/i)
  if (!match || !match[1]) {
    return undefined
  }

  const plainLabel = match[1].replace(/<[^>]*>/g, '')
  const normalized = normalizeJatsWhitespace(plainLabel)
  return normalized || undefined
}

const sections = computed(() => {
  const sourceSections = article.value?.sections ?? []

  return sourceSections.map((section) => {
    const title = section.title?.trim()
    if (!title) {
      return section
    }

    const leadingLabel = extractLeadingBodyLabel(section.bodytext)
    if (!leadingLabel) {
      return section
    }

    const mergedTitle = mergeLabelIntoHeading(leadingLabel, title)
    if (mergedTitle === title) {
      return section
    }

    return {
      ...section,
      title: mergedTitle,
    }
  })
})
const { tocSections, showContentOption, searchMode } = useJournalTocMode(sections, isLoading)
const tocSectionsForDisplay = computed(() =>
  tocSections.value.map((section) => {
    if (!section.title) {
      return section
    }

    const titleText = normalizeJatsWhitespace(
      $parseStringToDocument(section.title).body.textContent ?? '',
    )
    const title = journalSectionTitleWithoutFootnote(titleText)

    return title === section.title ? section : { ...section, title }
  }),
)
const selectedSectionId = ref<string | undefined>()
const title = computed(() => article.value?.title ?? '')
const articleTypeChipLabel = computed(() =>
  getJournalArticleTypeLabel(article.value?.articleType?.[0]),
)
const journalAbstractData = computed(() => getJournalAbstractData(article.value))
const hasOriginalPdf = computed(() => Boolean(article.value?.hasOriginalPdf))

const journalPdfUrl = computed(() => {
  const articleId = article.value?.id?.trim()
  if (!articleId) {
    return ''
  }

  return `/api/download/journals/${encodeURIComponent(articleId)}`
})

const disableDownloadLabel = computed(() => {
  return undefined
})

const isGeneratingPdf = ref(false)

async function openJournalDownload() {
  if (isGeneratingPdf.value) {
    return
  }

  const articleId = article.value?.id?.trim()
  if (!articleId) {
    return
  }

  if (hasOriginalPdf.value) {
    window.open(journalPdfUrl.value, '_blank', 'noopener')
    return
  }

  try {
    isGeneratingPdf.value = true
    toast.info('Nedlasting startet')

    const generatedPdfResponse = await fetch('/api/download/journals', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ articleId }),
    })

    if (!generatedPdfResponse.ok) {
      throw new Error(
        `Failed to generate PDF: ${generatedPdfResponse.status} ${generatedPdfResponse.statusText}`,
      )
    }

    const contentDisposition = generatedPdfResponse.headers.get('content-disposition')
    const filenameMatch = contentDisposition?.match(/filename="([^"]+)"/)
    const filename = filenameMatch?.[1] ?? 'tidsskrift.pdf'

    const generatedBlob = await generatedPdfResponse.blob()
    const generatedUrl = URL.createObjectURL(generatedBlob)
    try {
      const link = document.createElement('a')
      link.href = generatedUrl
      link.download = filename
      link.style.display = 'none'
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
    } finally {
      URL.revokeObjectURL(generatedUrl)
    }
    toast.success('Nedlasting fullført')
  } catch (downloadError) {
    console.error('Failed to download journal article PDF:', downloadError)
    toast.error('Noe gikk galt ved nedlasting. Prøv igjen.')
  } finally {
    isGeneratingPdf.value = false
  }
}

const selectedSearchResult = ref<{ id: string; highlights: string[] } | null>(null)

function handleShowSearchResult(id: string, highlights: string[]) {
  selectedSearchResult.value = { id, highlights }
  back()
}

function handleTocSectionSelected() {
  back()
}

function handleVisibleSectionChanged(sectionId: string | undefined) {
  if (!sectionId) {
    return
  }

  selectedSectionId.value = sectionId
}

const searchStrategy = useJournalArticleSearchStrategy()
const searchContext = computed<SearchContext>(() => ({
  contentId: article.value?.id || '',
  contentType: 'journal_article',
}))

const contentDiv = useTemplateRef<ComponentPublicInstance | null>('contentDiv')
const { contentTop, bannerHeight, contentBottom, headerHeight, updateMeasurements } =
  useContentMeasurements(contentDiv)

onMounted(() => {
  updateMeasurements()
  if (isMobileViewport() && route.query.q) {
    open('toc')
  }
})

const journalDisplayName = computed(
  () => publisherId.value.charAt(0).toUpperCase() + publisherId.value.slice(1).toLowerCase(),
)

const breadcrumbOverrides = computed(() => {
  const segments = route.path.split('/')

  return segments.map((_, index) => {
    if (index < 2) {
      return undefined
    }

    if (index === 2) {
      return {
        label: journalDisplayName.value,
        to: `/tidsskrifter/${publisherId.value}`,
      }
    }

    if (props.variant === 'issue') {
      if (index === 3) {
        return { label: `Utgave ${issueNumber.value}`, to: `/tidsskrifter/${publisherId.value}` }
      }
      if (index === 4) {
        return { label: title.value, to: route.path }
      }
      return false
    }

    if (index === 3) {
      return { label: `Volum ${volume.value} | Utgave ${issue.value}`, to: '' }
    }
    if (index === 4) {
      return false
    }
    if (index === 5) {
      return { label: title.value, to: route.path }
    }
    return false
  })
})

useTrackLastActivity(title)

usePendoContentTracking({
  type: 'journal_article',
  title,
  id: computed(() => trackableJournalArticleId(article.value?.id)),
  legalAreas: computed(() => trackableLegalAreas(!!article.value, journal.value?.legal_areas)),
})

useHead({
  bodyAttrs: { class: 'bg-surface-primary reading-dark-scope' },
  title,
})
</script>

<template>
  <div>
    <ContentDiv ref="contentDiv" class="flex items-center">
      <div class="nav:hidden flex items-center gap-2xs">
        <ControlButton
          :icon="faArrowLeft"
          type="outlined"
          size="small"
          :aria-label="`Tilbake til ${journalDisplayName}`"
          class="shrink-0 !p-2xs bg-transparent hover:bg-transparent dark:hover:bg-surface-secondary border rounded-lg"
          @click="
            navigateTo({ name: 'journal-publication', params: { publisherId: [publisherId] } })
          "
        />
        <span class="text-xsmall text-primary" aria-hidden="true">{{ journalDisplayName }}</span>
      </div>
      <div class="hidden nav:block min-w-0">
        <Breadcrumbs :overrides="breadcrumbOverrides" hide-mobile-back />
      </div>
      <ReadingActions
        v-if="article"
        :content="article"
        :disable-download-label="disableDownloadLabel"
        enable-dark-mode
        :dark-mode="readingDarkMode"
        @open-download="openJournalDownload"
        @update:dark-mode="toggleReadingDarkMode"
        :journal-issues-link="{
          name: 'journal-publication',
          params: { publisherId: [publisherId] },
        }"
      />
    </ContentDiv>

    <div v-if="isLoading" class="mt-l">
      <Skeleton />
    </div>
    <div v-else-if="error" class="mt-l">
      <StatusMessage status="error" title="Kunne ikke laste artikkel" />
    </div>
    <div v-else-if="!article" class="mt-l">
      <StatusMessage status="info" title="Fant ikke artikkel" />
    </div>

    <ReadingLayout
      v-if="article"
      v-model:sidebar-expanded="sidebarExpanded"
      fixed-content
      :content-top="contentTop"
      :content-bottom="contentBottom"
      :header-height="headerHeight"
      :banner-height="bannerHeight"
      aside-width="narrow"
      :licensed-content="hasLicense"
      context="journal"
      :active-view="activeView"
    >
      <template #sidebar>
        <JournalsMobileTableOfContentBar
          v-model:search-mode="searchMode"
          :show-content-option="showContentOption"
          mode="close"
        />
        <TableOfContentToggle
          v-model:search-mode="searchMode"
          title="Innhold"
          :show-search-option="true"
          :show-content-option="showContentOption"
          class="hidden md:flex sticky top-0 z-[9] bg-surface-elevated pb-xs"
        />
        <DocumentSearchUI
          v-if="searchMode && hasLicense"
          :search-mode="searchMode"
          :strategy="searchStrategy"
          :context="searchContext"
          @show-search-result="handleShowSearchResult"
        />
        <TableOfContentItems
          v-else-if="tocSections.length"
          v-model="selectedSectionId"
          :sections="tocSectionsForDisplay"
          :disabled="!hasLicense"
          @select="handleTocSectionSelected"
        />
        <p v-else class="text-caption text-secondary p-2xs">Innhold og søk krever abonnement</p>
      </template>

      <template #sidebar-collapsed>
        <span class="text-small text-carbon-500 dark:text-carbon-50 select-none font-bold"
          >Innholdsfortegnelse og søk</span
        >
      </template>

      <template #content="{ isScrollTop }">
        <JournalsMobileTableOfContentBar
          v-model:search-mode="searchMode"
          :show-content-option="showContentOption"
          mode="open"
        />
        <ReadingNotAuthorized v-if="!hasLicense" />
        <div v-else-if="sections.length" class="reading-content">
          <JournalsArticleContent
            :article-id="article.id"
            :article-title="title"
            :article-authors="article.author"
            :article-first-page="article.firstPage"
            :article-last-page="article.lastPage"
            :journal="journal"
            :footnotes="article.footnotes"
            :sections="sections"
            :search-result="selectedSearchResult"
            :abstract-data="journalAbstractData"
            :copyright-text="article.copyrightText"
            @visible-section-changed="handleVisibleSectionChanged"
          >
            <template #header>
              <div
                class="flex flex-col gap-xs"
                :class="{ 'pt-xs': articleTypeChipLabel && isScrollTop }"
              >
                <Chip
                  v-if="articleTypeChipLabel && isScrollTop"
                  variant="filled"
                  color="moss"
                  size="small"
                >
                  {{ articleTypeChipLabel }}
                </Chip>
                <ReadingHeader
                  v-if="article"
                  :content="article"
                  :is-scroll-top="isScrollTop"
                  :class="{ 'pt-xs': !(articleTypeChipLabel && isScrollTop) }"
                />
              </div>
            </template>
          </JournalsArticleContent>
        </div>
        <div v-else class="mt-l">
          <StatusMessage status="info" title="Ingen innholdseksjoner" />
        </div>
      </template>
    </ReadingLayout>
  </div>
</template>
